import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* The control's own tokens, imported HERE rather than left to the page. */
import "./tokens.css";

import { cn } from "./cn";
import { ChevronDown, CheckIcon } from "../../../assets/ui/icons";

/* A select whose OPEN LIST we draw ourselves — F-105. */

const TYPEAHEAD_RESET_MS = 1000;

export const normaliseOptions = (options = []) =>
  (options || []).map((o) =>
    typeof o === "object" && o !== null ? o : { value: o, label: String(o) }
  );

/* forwardRef exposes the TRIGGER, not the wrapper: callers that held a ref to
   the native <select> did so to focus it — the first-error scroll on the
   company-not-found modal, for one — and a wrapper div cannot take focus. */
export const SelectMenu = forwardRef(({
  id,
  value,
  onChange = () => {},
  options = [],
  placeholder,
  prefix,
  error,
  required = false,
  size = "md",
  disabled = false,
  className = "",
  "aria-describedby": describedBy,
  ...rest
}, forwardedRef) => {
  const items = normaliseOptions(options);
  const selected = items.find((o) => String(o.value) === String(value));

  const [open, setOpen] = useState(false);
  /* Which row the keyboard is on. Separate from the selection: moving through
     the list must not commit until Enter, exactly as a native listbox. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState(null);

  const triggerRef = useRef(null);
  const setTriggerRef = (node) => {
    triggerRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };
  const listRef = useRef(null);
  const typed = useRef({ text: "", at: 0 });

  const listId = id ? `${id}-listbox` : undefined;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  /* The trigger moves with the page, so the menu has to follow it or hang in
     the wrong place. Capture phase catches scrolling ancestors too, not just
     the window. */
  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  const close = useCallback(({ refocus = true } = {}) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  /* A click anywhere else closes it. The trigger is excluded so its own click
     toggles rather than closing and reopening. */
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      close({ refocus: false });
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  const commit = (option) => {
    onChange({ target: { value: option.value, name: rest?.name } });
    close();
  };

  const openAt = (index) => {
    setOpen(true);
    setActiveIndex(index);
  };

  const selectedIndex = items.findIndex((o) => String(o.value) === String(value));

  /* Typing jumps to a matching option, open or shut — what a native select
     does. The buffer accumulates while the keys keep coming. */
  const typeAhead = (key) => {
    const now = Date.now();
    const text =
      now - typed.current.at > TYPEAHEAD_RESET_MS ? key.toLowerCase() : typed.current.text + key.toLowerCase();
    typed.current = { text, at: now };

    const from = open ? activeIndex : selectedIndex;
    const ordered = [...items.slice(from + 1), ...items.slice(0, from + 1)];
    const hit = ordered.find((o) => String(o.label).toLowerCase().startsWith(text));
    if (!hit) return;
    const index = items.indexOf(hit);
    if (open) setActiveIndex(index);
    else commit(hit);
  };

  const onKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "Tab") {
      if (open) close({ refocus: false });
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        openAt(selectedIndex >= 0 ? selectedIndex : 0);
        return;
      }
      const step = e.key === "ArrowDown" ? 1 : -1;
      const next = Math.min(items.length - 1, Math.max(0, activeIndex + step));
      setActiveIndex(next);
      return;
    }
    if (open && (e.key === "Home" || e.key === "End")) {
      e.preventDefault();
      setActiveIndex(e.key === "Home" ? 0 : items.length - 1);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        openAt(selectedIndex >= 0 ? selectedIndex : 0);
        return;
      }
      if (items[activeIndex]) commit(items[activeIndex]);
      return;
    }
    /* A single printable character is type-ahead; anything with a modifier is
       a shortcut and is left to the browser. */
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      typeAhead(e.key);
    }
  };

  /* The keyboard row is scrolled into view, or a long list leaves it off
     screen while the highlight moves. */
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const row = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    /* Guarded: scrollIntoView is missing in jsdom and in older browsers, and
       an unreachable highlight is a far smaller problem than a render that
       throws. */
    if (typeof row?.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [open, activeIndex]);

  const errorId = id ? `${id}-error` : undefined;

  return (
    /* `u1-select` carries the control's own copy of the design tokens, so it
       renders identically on a legacy screen that is not inside .u1-root. */
    <div className={cn("u1-select relative", className)}>
      <button
        ref={setTriggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : describedBy}
        disabled={disabled}
        onClick={() => (open ? close() : openAt(selectedIndex >= 0 ? selectedIndex : 0))}
        onKeyDown={onKeyDown}
        className={cn(
          "flex w-full items-center rounded-[var(--radius-field)] border bg-white text-left font-sans",
          "transition-[border-color,box-shadow] duration-150",
          "focus:outline-none focus:border-[var(--orange)] focus:shadow-[0_0_0_4px_var(--focus-ring)]",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          size === "sm" ? "text-[13px] py-[9px] pr-8" : "text-[14px] py-[11px] pr-9",
          prefix ? "pl-11" : size === "sm" ? "pl-3" : "pl-3.5",
          error
            ? "border-[var(--coral)]"
            : "border-[var(--field-border)] hover:border-[var(--field-border-hover)]"
        )}
        {...rest}
      >
        <span
          className={cn(
            "block min-w-0 truncate",
            selected ? "text-[var(--ink)]" : "text-[var(--muted-2)]"
          )}
        >
          {selected ? selected.label : placeholder || ""}
        </span>
      </button>

      {prefix && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[16px] leading-none"
        >
          {prefix}
        </span>
      )}
      <ChevronDown
        size={size === "sm" ? 11 : 12}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]"
      />

      {open &&
        rect &&
        createPortal(
          <div
            className="u1-root u1-select absolute z-[120]"
            style={{ top: rect.top + 6, left: rect.left, width: rect.width }}
          >
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-labelledby={id}
              className={cn(
                "m-0 max-h-[280px] list-none overflow-y-auto rounded-xl border p-1.5",
                "border-[var(--line)] bg-white shadow-[0_12px_32px_rgba(20,40,60,.16)]"
              )}
            >
              {items.length === 0 && (
                <li className="px-3 py-2.5 text-[13px] text-[var(--muted)] font-sans">
                  Nothing to choose from.
                </li>
              )}
              {items.map((o, i) => {
                const isSelected = String(o.value) === String(value);
                return (
                  <li key={o.value} role="none">
                    <button
                      type="button"
                      role="option"
                      data-index={i}
                      aria-selected={isSelected}
                      /* The trigger keeps focus while the list is open, so the
                         row is highlighted rather than focused — that is what
                         lets one keydown handler drive the whole control. */
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => commit(o)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left",
                        "text-[13.5px] font-sans transition-colors",
                        i === activeIndex
                          ? "bg-[var(--coral-soft)] text-[var(--ink)]"
                          : "text-[var(--ink-2)]",
                        isSelected && "font-semibold text-[var(--ink)]"
                      )}
                    >
                      <span className="min-w-0 grow truncate">{o.label}</span>
                      {isSelected && (
                        <CheckIcon size={14} className="shrink-0 text-[var(--coral)]" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )}
    </div>
  );
});

SelectMenu.displayName = "SelectMenu";

export default SelectMenu;
